/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { Workbook } from 'exceljs';
import * as festoLogoIMG from '../../img/logo';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { FILENAME, downloadFile } from './ExportService';
import { getCompleteMediaResistances } from '../../api/MediaResistanceAPI';
import { econdaClickEvent, econdaDownloadEvent } from '../../Econda';
import { LoadingSpinner } from 'festo-react-ui-kit';
import { resetCompleteValues } from '../../features/media-resistance/MediaResistanceSlice';

interface exportExcelProps {
  language: string;
  isDisabled: boolean;
  isDownloadExcel: boolean;
  setIsDownloadExcel: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function ExportExcel({ language, isDisabled, isDownloadExcel, setIsDownloadExcel }: exportExcelProps): JSX.Element {
  const EXCEL_BLOB_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const EXCEL_FILE_EXTENSION = '.xlsx';

  const dispatch = useDispatch();
  const t = useTranslation();
  const mediaResistances = useSelector((state: RootState) => state.mediaResistances);
  const materialCategories = useSelector((state: RootState) => state.materialCategories);
  const medias = useSelector((state: RootState) => state.medias);
  const materials = useSelector((state: RootState) => state.materials);
  const resistanceTypes = useSelector((state: RootState) => state.resistanceTypes);

  useEffect(() => {
    function setHeaderDataAndStyle(worksheet: any) {
      const header = [
        { header: 'Festo', key: 'festo', width: 32 },
        { header: t('mediaDatagridHeader'), key: 'media', width: 32 },
        { header: t('formulaDatagridHeader'), key: 'formula', width: 28 },
        { header: t('concentrationDatagridHeader') + ' %', key: 'concentration', width: 20 },
        { header: t('temperatureDatagridHeader') + ' °C', key: 'temperature', width: 20 },
        { header: t('materialCategoryDatagridHeader'), key: 'materialCategory', width: 27 },
        { header: t('materialFilter'), key: 'material', width: 35 },
        { header: t('filterResistanceType'), key: 'resistance', width: 28 },
      ];
      worksheet.columns = header;
      setHeaderDataStyle(worksheet);
    }

    function setHeaderDataStyle(worksheet: any) {
      worksheet.getRow(1).font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).eachCell((c: any) => {
        const cell = c;
        cell.fill = {
          type: 'pattern',
          fgColor: { argb: '0091dc' },
          pattern: 'solid',
          bgColor: { argb: 'FFFFFFFF' },
        };
      });

      // First cell
      worksheet.getCell('A1').fill = {
        type: 'pattern',
        fgColor: { argb: 'FFFFFFFF' },
        pattern: 'solid',
        bgColor: { argb: 'FFFFFFFF' },
      };
    }

    function setBodyDataAndStyle(worksheet: any) {
      mediaResistances.completeValues.forEach((mediaResistance) => {
        const resistance = resistanceTypes.values.find((value) => value.resistance === mediaResistance.resistance);
        worksheet.addRow({
          media: t(mediaResistance.media.translationKey),
          formula: mediaResistance.media.formula,
          concentration: mediaResistance.media.concentration,
          temperature: mediaResistance.media.temperature,
          materialCategory: t(mediaResistance.material.materialCategory.translationKey),
          material: t(mediaResistance.material.translationKey),
          resistance: t(resistance ? resistance.translationKey : ''),
        });
      });
    }

    function addLogoAndInfo(workbook: any, worksheet: any) {
      const logo = workbook.addImage({
        base64: festoLogoIMG.logoBase64,
        extension: 'png',
      });
      worksheet.addImage(logo, {
        tl: { col: 0.09, row: 0.15 },
        ext: { width: 84, height: 15 },
      });
      worksheet.getCell('A2').value = t('headline');
      worksheet.getCell('A2').font = { name: 'Arial', size: 14, bold: true, color: { argb: '000000' } };
    }

    function addUrlToMediaResistance(worksheet: any) {
      worksheet.getCell('A3').value = {
        text: t('linkToMediaResistanceText'),
        hyperlink: window.location.href,
        tooltip: window.location.href,
      };
      worksheet.getCell('A3').font = {
        name: 'Arial',
        size: 10,
        underline: true,
        bold: true,
        color: { argb: '0000FF' },
      };
    }

    function generateAndDownloadExcel() {
      // Create woorkbook
      const workbook = new Workbook();
      workbook.creator = 'Media Resistance';
      workbook.created = new Date();
      workbook.modified = new Date();

      // Create worksheet
      const worksheet = workbook.addWorksheet(t('headline'), { views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }] });

      // Set Header
      setHeaderDataAndStyle(worksheet);

      // Set Body
      setBodyDataAndStyle(worksheet);

      // Add Logo
      addLogoAndInfo(workbook, worksheet);

      // Add Media Resistance URL
      addUrlToMediaResistance(worksheet);

      // Download
      workbook.xlsx.writeBuffer().then((workbook) => {
        downloadFile(workbook, EXCEL_BLOB_TYPE, t(FILENAME) + EXCEL_FILE_EXTENSION);
      });
      setIsDownloadExcel(false);
      dispatch(resetCompleteValues());
    }

    if (isDownloadExcel && mediaResistances.completeValues.length > 0) {
      generateAndDownloadExcel();
    }
  }, [dispatch, isDownloadExcel, mediaResistances.completeValues, resistanceTypes.values, setIsDownloadExcel, t]);

  const getArrayOfNumbers = (array: (string | number)[]): number[] => {
    const newArray = array.map((number) => Number(number));
    return newArray;
  };

const disableClassname = (isDownloadExcel || isDisabled) && 'download--disable'

  
  return (
      <a
        href="#download-excel"
        onClick={(e) => {
          if (isDownloadExcel || isDisabled) { e.preventDefault(); return; }
          setIsDownloadExcel(true);
          getCompleteMediaResistances(
            {
              language: language,
              mediaIds: getArrayOfNumbers(medias.selectedIds),
              materialIds: getArrayOfNumbers(materials.selectedIds),
              materialCategoryIds: getArrayOfNumbers(materialCategories.selectedIds),
            },
            resistanceTypes.selected,
            dispatch,
          );
          econdaDownloadEvent('download', 'product information xls', 'blind menu', 'download xls - ' + t(FILENAME));
          econdaClickEvent('download buttons', 'general', 'blind menu', t('excelText'));
        }}
          className={`one-line pointer ${disableClassname ?? ""}`}      >
        <div className="fwe-icon fwe-icon-documents-doc-xls marging-right-link"></div>
        <div className="hide-text font-size--normal">{t('excelText')}</div>
        {isDownloadExcel && <LoadingSpinner text={t('downloading')} />}
      </a>
  );
}
